import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  DollarSign,
  FileSignature,
  FlaskConical,
  KeyRound,
  LogOut,
  Menu,
  Target,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Admin, Dealership } from "@lumika/shared";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  LumikaLogo,
  MobileAppBar,
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
  SkipLink,
  toast,
} from "@lumika/ui";
import { adminApi } from "../api/admin";
import { AdminGlobalSearch } from "../components/AdminGlobalSearch";
import { DealershipSwitcher } from "./DealershipSwitcher";

type NavigationItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: number;
};

export function AdminShell({ admin }: { admin: Admin }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeDealershipId = getActiveDealershipId(location.pathname);
  const activeDealershipQuery = useQuery({
    queryKey: ["dealership", activeDealershipId],
    queryFn: () => adminApi.getDealership(activeDealershipId!),
    enabled: Boolean(activeDealershipId),
  });
  const outreachSummaryQuery = useQuery({
    queryKey: ["outreach", "summary"],
    queryFn: adminApi.outreachSummary,
  });
  const activeDealer = activeDealershipQuery.data
    ? pickDealershipContext(activeDealershipQuery.data)
    : null;
  const adminName = admin.displayName ?? admin.email;
  const dueCount = outreachSummaryQuery.data?.dueCount ?? 0;

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const signOut = async () => {
    try {
      await adminApi.logout();
    } catch {
      toast.error("Couldn't sign out — try again.");
      return;
    }
    window.location.assign("/sign-in");
  };

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground md:grid md:grid-cols-[272px_minmax(0,1fr)] md:overflow-hidden">
      <SkipLink />
      <MobileAppBar
        leading={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        }
        title={activeDealer?.name ?? getRouteTitle(location.pathname)}
        trailing={<AdminGlobalSearch role={admin.role} variant="mobile" />}
      />

      <aside className="hidden h-dvh min-h-0 flex-col border-r bg-card md:flex">
        <ShellHeader />
        <div className="px-4 pb-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Dealership context</p>
          <DealershipSwitcher activeDealer={activeDealer} />
        </div>
        <div className="px-4 pb-4">
          <AdminGlobalSearch role={admin.role} />
        </div>
        <Navigation dueCount={dueCount} />
        <AdminFooter adminName={adminName} onSignOut={signOut} />
      </aside>

      <main
        id="main-content"
        tabIndex={-1}
        className="min-w-0 flex-1 overflow-y-auto md:h-dvh"
      >
        <Outlet />
      </main>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="gap-0 px-0 md:hidden">
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <ShellHeader onNavigate={() => setDrawerOpen(false)} />
          <div className="px-4 pb-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Dealership context</p>
            <DealershipSwitcher activeDealer={activeDealer} />
          </div>
          <SheetBody className="flex flex-col px-0 py-0">
            <Navigation dueCount={dueCount} onNavigate={() => setDrawerOpen(false)} />
          </SheetBody>
          <AdminFooter adminName={adminName} onSignOut={signOut} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ShellHeader({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <header className="flex h-16 shrink-0 items-center px-4">
      <Link
        to="/dealerships"
        onClick={onNavigate}
        className="flex items-center gap-2.5 font-semibold tracking-tight"
      >
        <LumikaLogo theme="auto" size={28} />
        <span>lumika admin</span>
      </Link>
    </header>
  );
}

function Navigation({
  dueCount,
  onNavigate,
}: {
  dueCount: number;
  onNavigate?: () => void;
}) {
  const groups: Array<{ label: string; items: NavigationItem[] }> = [
    {
      label: "Workspace",
      items: [
        { label: "Dealerships", to: "/dealerships", icon: Building2 },
        { label: "Contracts", to: "/contracts", icon: FileSignature },
        { label: "Outreach", to: "/outreach", icon: Target, badge: dueCount },
      ],
    },
    {
      label: "Operations",
      items: [
        { label: "Operations & health", to: "/ops", icon: Activity },
        { label: "SFTP access", to: "/sftp", icon: KeyRound },
      ],
    },
    {
      label: "Finance",
      items: [{ label: "Billing", to: "/billing", icon: DollarSign }],
    },
    {
      label: "Developer tools",
      items: [
        { label: "Channel playground", to: "/channels-playground", icon: FlaskConical },
      ],
    },
  ];

  return (
    <nav aria-label="Admin sections" className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
      {groups.map((group) => (
        <div key={group.label} className="mb-5 last:mb-0">
          <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">{group.label}</p>
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    [
                      "flex min-h-touch items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-accent font-medium text-accent-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    ].join(" ")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                  {item.badge && item.badge > 0 ? (
                    <Badge variant="warning" className="ml-auto shrink-0">
                      {item.badge}
                    </Badge>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function AdminFooter({
  adminName,
  onSignOut,
}: {
  adminName: string;
  onSignOut: () => void;
}) {
  return (
    <footer className="flex shrink-0 items-center justify-between gap-2 border-t px-4 py-3 text-sm">
      <span className="min-w-0">
        <span className="block truncate font-medium" title={adminName}>
          {adminName}
        </span>
        <span className="block text-xs text-muted-foreground">Platform admin</span>
      </span>
      <Button type="button" variant="ghost" size="icon" aria-label="Sign out" onClick={onSignOut}>
        <LogOut className="h-4 w-4" />
      </Button>
    </footer>
  );
}

function getActiveDealershipId(pathname: string): string | null {
  const dealershipId = pathname.match(/^\/dealerships\/([^/]+)/)?.[1];
  return dealershipId && dealershipId !== "new" ? dealershipId : null;
}

function pickDealershipContext(
  dealership: Dealership,
): Pick<Dealership, "id" | "name" | "status"> {
  return {
    id: dealership.id,
    name: dealership.name,
    status: dealership.status,
  };
}

function getRouteTitle(pathname: string): string {
  if (pathname.startsWith("/contracts")) return "Contracts";
  if (pathname.startsWith("/outreach")) return "Outreach";
  if (pathname.startsWith("/billing")) return "Platform billing";
  if (pathname.startsWith("/ops")) return "Operations & health";
  if (pathname.startsWith("/sftp")) return "SFTP access";
  if (pathname.startsWith("/channels-playground")) return "Channel playground";
  if (pathname === "/dealerships/new") return "Create dealership";
  return "Dealerships";
}
